import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, GraduationCap } from 'lucide-react';
import Input from '@ajat/design-system/Input';
import Button from '@ajat/design-system/Button';
import { HeadingMedium } from '@ajat/design-system/Configurator';
import useSchools from '../../../../hooks/useSchools';
import { useCart } from '../../../../context/CartContext';
import { saveSchoolSelection } from '../../../../services/schoolSelectionStore';
import './SchoolClassGate.css';

/**
 * SchoolClassGate — the inline "pick your school and class first" panel.
 *
 * Shown in the product page's right column INSTEAD of the configurator while the
 * visitor has no school + class (CLS-DK: an anonymous visitor may look at a product,
 * but options, prices and the cart depend on the school's shop and the class).
 * School → Shop (only when the school has several open shops) → Class → button.
 *
 * On submit it does what the header's school popup does — persists the selection
 * (services/schoolSelectionStore) and binds the cart to school / shop / class — and
 * then hands the selection to `onSelected`, which decides where the visitor goes.
 *
 * Only schools with an open shop are listed, and only the classes enabled in the
 * chosen shop: the same lists as the popup.
 *
 * Looks and behaves like the "Din skole" step of the Open Classshop page
 * (CLS-DK CreateSchool): a `.pp__section` with a HeadingMedium, the same DS
 * searchable inputs in mobile focus mode (search icon on the school, cap on the
 * class — as in the header's school popup) and a class field that accepts a
 * typed class name.
 */
const SchoolClassGate = ({
  brandSlug,
  storageKey,
  prefill = null,          // { schoolSlug, shopSlug } — e.g. a school-shop URL
  onSelected,
  title = '',   // HeadingMedium of the section ("Din skole")
  text = '',    // optional explanation under the heading (green info box, as in the school popup)
  schoolLabel = 'School',
  shopLabel = 'Shop',
  classLabel = 'Class',
  classOptionLabel = (c) => c,
  buttonLabel = 'Continue',
  busyLabel = '…',
  showShopLine = false,    // "Butik: <name>" when the school has one open shop — hidden by default
}) => {
  const { schools, loading } = useSchools(brandSlug);
  const { setSchool: setCartSchool } = useCart(brandSlug);

  const openSchools = useMemo(
    () => (schools ?? []).filter((s) => (s.seasons ?? []).length > 0),
    [schools],
  );

  const [schoolId, setSchoolId] = useState(null);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [shopId, setShopId] = useState(null);
  const [shopSearch, setShopSearch] = useState('');
  const [className, setClassName] = useState('');
  const [classSearch, setClassSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const school = useMemo(() => openSchools.find((s) => s.id === schoolId) ?? null, [openSchools, schoolId]);
  const shops = school?.seasons ?? [];
  const shop = useMemo(
    () => (shops.length === 1 ? shops[0] : shops.find((sh) => String(sh.id) === String(shopId)) ?? null),
    [shops, shopId],
  );

  const pickSchool = useCallback((s, shopSlug = null) => {
    setSchoolId(s?.id ?? null);
    setSchoolSearch(s?.name ?? '');
    const wanted = shopSlug ? (s?.seasons ?? []).find((sh) => sh.slug === shopSlug) : null;
    setShopId(wanted?.id ?? null);
    setShopSearch(wanted?.name ?? '');
    setClassName('');
    setClassSearch('');
  }, []);

  // A URL that already names the school (and the shop): start on the class step.
  useEffect(() => {
    if (loading || schoolId || !prefill?.schoolSlug) return;
    const s = openSchools.find((x) => x.slug === prefill.schoolSlug);
    if (s) pickSchool(s, prefill.shopSlug ?? null);
  }, [loading, schoolId, prefill?.schoolSlug, prefill?.shopSlug, openSchools, pickSchool]);

  const schoolOptions = useMemo(() => openSchools.map((s) => ({ label: s.name, value: s.id })), [openSchools]);
  const shopOptions = useMemo(() => shops.map((sh) => ({ label: sh.name, value: sh.id })), [shops]);
  const classOptions = useMemo(
    () => (shop?.classes ?? []).map((c) => ({ label: classOptionLabel(c), value: c })),
    [shop, classOptionLabel],
  );

  // A typed class counts once it names one of the shop's classes (as on the
  // Open Classshop page, where "Done" on the keyboard keeps what was typed) —
  // but only a listed class: the cart is priced by it.
  const matchClass = useCallback((typed) => {
    const t = typed.trim().toLowerCase();
    if (!t) return '';
    const hit = classOptions.find((o) => String(o.value).toLowerCase() === t || String(o.label).toLowerCase() === t);
    return hit ? hit.value : '';
  }, [classOptions]);

  const ready = Boolean(school && shop && className);

  const submit = useCallback(async () => {
    if (!ready || busy) return;
    setBusy(true);
    saveSchoolSelection(storageKey, school, shop, className);
    try {
      await setCartSchool({ school_id: school.id, shop_id: Number(shop.id), school_class: className });
    } catch { /* cart sync is best-effort, as in the popup */ }
    try {
      await onSelected?.({ school, season: shop, class: className });
    } finally {
      setBusy(false);
    }
  }, [ready, busy, storageKey, school, shop, className, setCartSchool, onSelected]);

  return (
    <div className="pp__section school-gate" data-testid="school-class-gate">
      {title && <HeadingMedium title={title} titleVariant="h2" />}
      {text && (
        <div className="school-gate__info">
          <p className="school-gate__info-text typo-body-lg">{text}</p>
        </div>
      )}

      <div className="school-gate__card">
        <Input
          type="searchable"
          label={schoolLabel}
          value={schoolSearch}
          options={schoolOptions}
          onChange={(e) => setSchoolSearch(e.target.value)}
          onSelect={(opt) => pickSchool(opt ? openSchools.find((s) => s.id === opt.value) : null)}
          showOptionIcons={false}
          mobileFocus
          disabled={loading}
        />

        {/* Shop: a dropdown when the school has several open shops, a read-only
            line when it has exactly one — that line is hidden for now
            (`showShopLine`), the shop is still picked automatically. */}
        {school && shops.length > 1 && (
          <Input
            type="searchable"
            label={shopLabel}
            value={shopSearch}
            options={shopOptions}
            onChange={(e) => setShopSearch(e.target.value)}
            onSelect={(opt) => {
              setShopId(opt?.value ?? null);
              setShopSearch(opt?.label ?? '');
              setClassName('');
              setClassSearch('');
            }}
            showIcon={false}
            showOptionIcons={false}
            mobileFocus
          />
        )}
        {showShopLine && school && shops.length === 1 && (
          <div className="school-gate__shop typo-body">
            {shopLabel}: <strong>{shops[0].name}</strong>
          </div>
        )}

        {school && shop && (
          <Input
            type="searchable"
            label={classLabel}
            icon={<GraduationCap size={20} />}
            value={classSearch}
            options={classOptions}
            onChange={(e) => { setClassSearch(e.target.value); setClassName(matchClass(e.target.value)); }}
            onSelect={(opt) => {
              setClassName(opt?.value ?? '');
              setClassSearch(opt?.label ?? '');
            }}
            showOptionIcons={false}
            mobileFocus
          />
        )}

        <Button
          size="primary"
          variant="alternative"
          iconRight={<ChevronRight size={20} />}
          onClick={submit}
          disabled={!ready || busy}
        >
          {busy ? busyLabel : buttonLabel}
        </Button>
      </div>
    </div>
  );
};

export default SchoolClassGate;
